import React, { useState, cloneElement } from 'react';
import { motion } from 'framer-motion';
import { Package, Droplets, Truck, RotateCcw, Sparkles } from 'lucide-react';
const stages = [{
  id: 1,
  title: 'Lease',
  icon: <Package data-id="element-268" />,
  desc: 'Order the packaging you need on demand. No minimums, no capex.'
}, {
  id: 2,
  title: 'Use',
  icon: <Droplets data-id="element-269" />,
  desc: 'Fill bottles, pack crates, load pallets. Works with your existing operations.'
}, {
  id: 3,
  title: 'Distribute',
  icon: <Truck data-id="element-270" />,
  desc: 'Send it out to your customers. We keep an eye on every asset along the way.'
}, {
  id: 4,
  title: 'Return',
  icon: <RotateCcw data-id="element-271" />,
  desc: 'Empties come back via our network of drop-points and route collections.'
}, {
  id: 5,
  title: 'Sanitize',
  icon: <Sparkles data-id="element-272" />,
  desc: 'A thorough wash, a careful look-over, and a quality check before it goes round again.'
}];
export const HowItWorks: React.FC = () => {
  const [activeStage, setActiveStage] = useState(0);
  return <section id="how-it-works" className="py-32 bg-surface overflow-hidden" data-id="element-273">
      <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-274">
        <div className="text-center max-w-3xl mx-auto mb-20" data-id="element-275">
          <h2 className="font-display text-4xl md:text-5xl text-primary mb-6" data-id="element-276">
            The circular lifecycle.
          </h2>
          <p className="text-lg text-ink/70" data-id="element-277">
            One loop that takes the headache out of packaging. Your bottles,
            crates, and pallets keep moving, and we handle the parts in between.
          </p>
        </div>

        {/* Desktop Circular Diagram */}
        <div className="hidden lg:flex relative h-[600px] items-center justify-center" data-id="element-278">
          <div className="absolute w-[500px] h-[500px] rounded-full border-2 border-dashed border-leaf/30" data-id="element-279" />

          {stages.map((stage, index) => {
          const angle = (index * (360 / stages.length) - 90) * (Math.PI / 180);
          const radius = 250;
          const x = Math.cos(angle) * radius;
          const y = Math.sin(angle) * radius;
          const isActive = activeStage === index;
          return <motion.div key={stage.id} className="absolute flex flex-col items-center cursor-pointer" style={{
            x,
            y
          }} onHoverStart={() => setActiveStage(index)} initial={{
            opacity: 0,
            scale: 0.8
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} data-id="element-280">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center transition-all duration-500 ${isActive ? 'bg-leaf text-white scale-110 shadow-leaf-glow' : 'bg-white border border-primary/10 text-primary/60 hover:border-leaf/50 shadow-glossy'}`} data-id="element-281">
                  {cloneElement(stage.icon as React.ReactElement, {
                className: 'w-6 h-6'
              })}
                </div>
                <div className={`absolute top-20 w-48 text-center transition-opacity duration-300 ${isActive ? 'opacity-100' : 'opacity-0'}`} data-id="element-282">
                  <h4 className="font-display text-xl text-primary mb-2" data-id="element-283">
                    {stage.title}
                  </h4>
                  <p className="text-sm text-ink/70" data-id="element-284">{stage.desc}</p>
                </div>
              </motion.div>;
        })}

          {/* Center Content */}
          <div className="text-center max-w-[200px]" data-id="element-285">
            <span className="font-mono text-sm text-leaf tracking-widest uppercase mb-2 block" data-id="element-286">
              Stage {activeStage + 1}
            </span>
            <h3 className="font-display text-3xl text-primary" data-id="element-287">
              {stages[activeStage].title}
            </h3>
          </div>
        </div>

        {/* Mobile Vertical List */}
        <div className="lg:hidden space-y-8 relative before:absolute before:inset-y-0 before:left-8 before:w-px before:bg-leaf/30" data-id="element-288">
          {stages.map((stage, index) => <motion.div key={stage.id} initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.1
        }} className="relative flex gap-6 items-start" data-id="element-289">
              <div className="w-16 h-16 shrink-0 rounded-full bg-white border-2 border-leaf flex items-center justify-center text-leaf z-10 shadow-glossy" data-id="element-290">
                {stage.icon}
              </div>
              <div className="pt-3" data-id="element-291">
                <h4 className="font-display text-2xl text-primary mb-2" data-id="element-292">
                  {stage.title}
                </h4>
                <p className="text-ink/70" data-id="element-293">{stage.desc}</p>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
};